<p>Use to organize large datasets that users can scan, compare, filter, sort, paginate, and act on.</p>
<h2>Use When</h2>
<ul>
  <li>Displaying structured row-and-column data</li>
  <li>Supporting filtering, sorting, pagination, row actions, configurable columns, or custom row templates</li>
  <li>Handling local datasets or externally paged server-side data</li>
</ul>
<h2>Behavior</h2>
<ul>
  <li>Supports synchronous and asynchronous data loading</li>
  <li>Supports table-level and column-level filtering</li>
  <li>Supports pagination, sorting, row actions, batch selection, child rows, expandable rows, and column configuration</li>
  <li>
    Supports custom cell templates through <app-text-code>madColumnDef</app-text-code> and expandable templates through
    <app-text-code>madExpandableColumnDef</app-text-code>
  </li>
</ul>
<p>
  Import the
  <app-text-code>DataTableComponent</app-text-code>
  and use it via
  <app-text-code>&lt;mad-data-table&gt;</app-text-code>
  .
</p>

<section id="OVERVIEW">
  <h2>Examples</h2>
  <ul>
    <li><a href="{{ pageUrl + '#BASIC' }}">Basic Table</a>: Basic table with pagination and filter</li>
    <li><a href="{{ pageUrl + '#DATA_TRANSFORMATION' }}">Data transformation</a>: Custom transformation of column data</li>
    <li><a href="{{ pageUrl + '#SINGLE_MODE' }}">Single Mode</a>: Table with single mode actions</li>
    <li><a href="{{ pageUrl + '#BATCH_MODE' }}">Batch Mode</a>: Table with batch mode actions</li>
    <li>
      <a href="{{ pageUrl + '#ASYNC_LOADING' }}">Async Loading</a>: Table with asynchronous loading where sorting, filtering, and paging are
      handled server-side
    </li>
    <li><a href="{{ pageUrl + '#ROW_ID' }}">Row ID</a>: Return IDs instead of full rows on events</li>
    <li><a href="{{ pageUrl + '#CHILD_ROWS' }}">Child rows</a>: Use child rows with limited support</li>
    <li>
      <a href="{{ pageUrl + '#COLUMN_CONFIGURATION' }}">Column configuration</a>: Allow users to view and edit different column
      configurations
    </li>
    <li><a href="{{ pageUrl + '#TABLE_HEIGHT' }}">Table height</a>: Stretch the table to its parent height</li>
    <li><a href="{{ pageUrl + '#COLUMN_FILTER' }}">Column filter</a>: Filter table data from filterable column headers</li>
    <li><a href="{{ pageUrl + '#STATEFUL' }}">Stateful datatable</a>: Persist sort, filter, and page-size state</li>
    <li><a href="{{ pageUrl + '#CELL_TEMPLATES' }}">Cell templates</a>: Render custom cell content</li>
    <li><a href="{{ pageUrl + '#EXPANDABLE_TEMPLATE' }}">Expandable rows</a>: Render expandable row detail content</li>
  </ul>
  <p>At the <a href="{{ pageUrl + '#SUMMARY' }}">end of the page</a> you will find the API specification.</p>
</section>

<section id="BASIC">
  <h2>Basic table</h2>
  <p>The data table expects the following input:</p>
  <ul>
    <li>
      displayedColumns: definitions of columns of type
      <app-text-code>DataTableColumn</app-text-code>
      with the following properties
      <ul>
        <li><strong>label</strong> (required): label displayed in the table and translated when possible</li>
        <li><strong>dataPropertyName</strong> (required): property name of the column in the row data</li>
        <li><strong>isSortable</strong> (optional): enables sorting for the column</li>
        <li><strong>isRightAligned</strong> (optional): aligns column text to the right</li>
        <li>
          <strong>transformer</strong> (optional): transforms data before display. See
          <a href="{{ pageUrl + '#DATA_TRANSFORMATION' }}">Data Transformation</a>.
        </li>
        <li><strong>transformerParams</strong> (optional): params for the transformer function</li>
      </ul>
    </li>
  </ul>
  <example-viewer [example]="basicDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="DATA_TRANSFORMATION">
  <h2>Data transformation</h2>
  <p>
    To transform displayed data, set the optional <strong>transformer</strong> and <strong>transformerParams</strong> properties in the
    column definition. You can supply any function as transformer.
  </p>
  <ul>
    <li>
      Example: custom transformations for <em>Title</em> translation, <em>Gender</em> symbol display, and <em>Email</em> uppercase display
    </li>
    <li>
      Internal <strong>sort</strong> and <strong>filter</strong> use the <strong>displayed data</strong>, not the original data. For
      example, after translating titles, the filter will match translated values instead of the original values.
    </li>
    <li><strong>events</strong>, like row actions, still return the original row data</li>
  </ul>
  <example-viewer [example]="transformerDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="SINGLE_MODE">
  <h2>Single mode</h2>
  <p>
    If at least one row action exists with type <strong>SINGLE</strong> and no batch action exists, the table is displayed in single mode.
  </p>
  <ul>
    <li>All row actions are available in the action menu of the leftmost column</li>
    <li>Clicking any row triggers the default action, which is the first action</li>
    <li>Actions with type <strong>NONE</strong> are independent of rows and are displayed above the table, for example a create action</li>
  </ul>
  <example-viewer [example]="singleModeTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="BATCH_MODE">
  <h2>Batch mode (multiselect)</h2>
  <p>
    If at least one row action exists with type <strong>BATCH</strong>, the table is displayed in batch mode. You can force the display
    mode; the example below uses the batch selection button to switch between batch and single modes.
  </p>
  <ul>
    <li>Clicking a row selects or deselects the row</li>
    <li>All row actions are displayed above the table</li>
    <li>Action buttons are disabled when the amount of selected rows does not match the action type</li>
    <li>Batch actions show an indicator with the number of selected rows</li>
    <li>Clicking the checkbox in the header selects all rows of the current page</li>
  </ul>
  <example-viewer [example]="batchModeTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="ASYNC_LOADING">
  <h2>Asynchronous loading</h2>
  <p>Use asynchronous loading for large datasets when only one page of data should be loaded at a time.</p>
  <ul>
    <li>Because the table only has one page of data, filtering, sorting, and paging must be handled externally</li>
    <li>
      <strong>filtering</strong>: the internal filter is deactivated; create your own filter and update <strong>[page]</strong> and
      <strong>[tableData]</strong>
    </li>
    <li>
      <strong>sorting</strong>: subscribe to <strong>(sortEvent)</strong>, load the new page from the server, then update
      <strong>[page]</strong> and <strong>[tableData]</strong>
    </li>
    <li>
      <strong>paging</strong>: set the current page with <strong>[page]</strong> and subscribe to <strong>(pageEvent)</strong>, then load
      the requested page and update <strong>[page]</strong> and <strong>[tableData]</strong>
    </li>
    <li>Set <strong>[loading]</strong> to true while loading data to display a spinner</li>
  </ul>
  <example-viewer [example]="asyncDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="ROW_ID">
  <h2>IDs instead of rows</h2>
  <p>
    By default, row actions return the complete selected rows in the event. If events should return IDs instead of full row objects, provide
    an ID generator function to <strong>[idGenerator]</strong>.
  </p>
  <example-viewer [example]="idGeneratorDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="CHILD_ROWS">
  <h2>Child rows (limited support)</h2>
  <p>Rows can be marked as child rows. Child rows currently have limited support:</p>
  <ul>
    <li>Table actions for child rows are ignored in batch and single actions</li>
    <li>Automatic sorting does not work reliably for child rows; implement sorting logic externally when needed</li>
    <li>
      Provide <strong>[idGenerator]</strong> and <strong>[parentIdGenerator]</strong>. Rows with a generated parent ID are treated as child
      rows.
    </li>
  </ul>
  <example-viewer [example]="childRowsDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="COLUMN_CONFIGURATION">
  <h2>Custom column configuration</h2>
  <p>
    If you provide at least one definition for <strong>[columnDefinitions]</strong>, the table displays a button for users to configure
    columns. The definition can have a name, defaults to USER if omitted, and must define all available and selected columns.
  </p>
  <p>
    The data table emits <strong>(columnDefinitionChangeEvent)</strong> when a column definition is saved or deleted. Subscribe to this
    event when the user's column configuration should be persisted.
  </p>
  <p>If you provide more than one column definition template, users can choose which template they want to edit.</p>
  <example-viewer [example]="columnConfigurationDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="TABLE_HEIGHT">
  <h2>Stretch table to parent height</h2>
  <p>
    Set <strong>[tableClass]</strong> to <em>datatable-vertical-stretch-to-parent</em> to stretch the table to its parent height. The table
    has a minimum height and shows a scrollbar if it cannot display all rows while keeping pagination visible.
  </p>
  <example-viewer [example]="parentHeightDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="COLUMN_FILTER">
  <h2>Filter table based on columns</h2>
  <p>
    Use <strong>[filterMode]</strong> set to <em>COLUMN_BASED</em> to filter a table from its columns. Add <strong>isFilterable</strong> to
    each filterable column. The <strong>filterParams.filterOptions</strong> property can pass key-value options for predefined filters.
  </p>
  <example-viewer [example]="columnFilterDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="STATEFUL">
  <h2>Stateful datatable</h2>
  <p>
    Set <strong>[persistenceConfig]</strong> with <em>persistSort</em>, <em>persistFilter</em>, and <em>persistPageSize</em> to persist the
    table's sort, filter, and page-size state. Override <strong>DataTablePersistenceService</strong> and provide it through
    <strong>DATA_TABLE_PERSISTENCE_SERVICE_PROVIDER</strong> to use a custom implementation.
  </p>
  <example-viewer [example]="statefulDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="CELL_TEMPLATES">
  <h2>Cell templates for custom cell definitions</h2>
  <p>
    Use <strong>madColumnDef</strong> to define a column definition using the <strong>id</strong> of the DataTableColumn. Use
    <strong>madCellDef</strong> to define the matching cell definition. Use the <em>let element</em> syntax inside
    <strong>madCellDef</strong> to access the current row data.
  </p>
  <example-viewer [example]="cellTemplatesDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="EXPANDABLE_TEMPLATE">
  <h2>Template to make a row expandable</h2>
  <p>
    Use <strong>madExpandableColumnDef</strong> to define an expandable definition. Use <strong>madExpandableCellDef</strong> to define the
    matching expandable cell definition. Use the <em>let element</em> syntax inside <strong>madExpandableCellDef</strong> to access the
    current row data. Use <strong>[rowExpandable]</strong> to define which rows are expandable.
  </p>
  <example-viewer [example]="expandableTemplateDataTable"></example-viewer>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>

<section id="SUMMARY">
  <h2>API Specification</h2>
  <app-data-table-demo-api-spec></app-data-table-demo-api-spec>
  <a href="{{ pageUrl + '#OVERVIEW' }}">Back to overview</a>
</section>
